import { useEffect, useState } from 'react';
import './ReadySetGo.scss';

import { Helmet } from 'react-helmet';
import { Alert } from '@material-ui/lab';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { Button } from '@thrivetribe/gloji.ui.atoms.button';
import HeroImage from '../../../../assets/img/veronika.jpg';
import { CircularLoader } from '../../../atoms/Loaders';
import useUser from '../../../hooks/useUser';
import {
  ModalPageBody,
  ModalPageCopy,
  ModalPageFooter,
  ModalPageHeading,
  ModalPageHeroImage,
  ModalPageWrapper,
} from '../../../organisms/ModalPage';
import {
  CustomModal,
  ModalContent,
  ModalTitle,
} from '../../../organisms/CustomModal';
import { useSelector } from 'react-redux';
import { programmes } from '../../../../enums/programmes';
import PageContent from './PageContent';
import useUserId from '../../../hooks/useUserId';
import { mapCompleteProfileRequest } from '../ProfileBuilderJourneys/ConfigurableProfileBuilder/utils';
import { useGetProfileByIdQuery } from '../../../../redux/profileApiV3';
import { useCompleteProfileMutation } from '../../../../redux/profileApi';
import { profileBuilderConfig } from '../../../../configs/serviceConfig';
import { Programmes } from '../../../../data/Programme';
import { useCurrentJourney } from '../../../hooks/useCurrentJourney';
import { IsObjectEmptyOrNull } from '../../../../utils/IsEmpty';

const ReadySetGo = (props) => {
  const [user] = useUser();
  const [userId] = useUserId();
  const { programmeSlug } = props;

  const profileByIdResponse = useGetProfileByIdQuery(userId ?? skipToken);
  const currentJourney = useCurrentJourney(profileByIdResponse, programmeSlug);

  const storedProfileBuilder = useSelector(
    (state: any) => state.profileBuilder
  );
  const signupFormValues = useSelector(
    (state: any) => state.signupFlowData.signupFormValues
  );

  const currentProgrammeId = Programmes.find(
    (x) => x.slug === programmeSlug
  ).programmeId;

  const [open, setOpen] = useState(false);
  const [profilePostStatus, setProfilePostStatus] = useState({
    isLoading: false,
    isError: null,
  });
  const [completeProfile] = useCompleteProfileMutation();

  useEffect(() => {
    if (currentProgrammeId === programmes['lose-weight'].id) {
      window.gtag('event', 'page_view', {
        page_title: 'Ready Set Go! | Profile builder | Sign up | Gloji',
      });
    }
  }, [currentProgrammeId]);

  const handleModalClose = () => {
    setOpen(false);
    window.location.href = '/';
    document.getElementById('root').classList.remove('modal-active');

    //Trigger google analytics event at completion of profile builder journey
    if (currentProgrammeId === programmes['lose-weight'].id) {
      window.gtag('event', 'completeProfileBuilder', {
        event_category: 'Profile builder',
        event_label: 'Complete Profile Builder',
      });

      window.dataLayer.push({
        event: 'c_events',
        event_category: 'Signup',
        event_action: 'Profile Builder Complete',
      });
    }
  };

  const handleModalOpen = () => {
    setOpen(true);
    document.getElementById('root').classList.add('modal-active');
  };

  const handleLetsGo = async () => {
    if (
      currentProgrammeId === programmes['smoke-free'].id &&
      profileBuilderConfig.ServiceId === currentJourney.referringServiceId
    ) {
      const requestData = mapCompleteProfileRequest(
        userId,
        currentJourney.Id,
        currentJourney.referringServiceId,
        currentJourney.programmeId,
        storedProfileBuilder
      );

      try {
        await completeProfile(requestData).unwrap();
        window.location.href = '/';
      } catch (error) {
        setProfilePostStatus({ isLoading: false, isError: true });
      }

      return;
    }

    if (currentProgrammeId === programmes['smoke-free'].id) {
      setProfilePostStatus({ isLoading: true, isError: null });
      await completeProfile({ userId: userId, journeyId: currentJourney.id })
        .unwrap()
        .then(() => {
          setProfilePostStatus({ isLoading: false, isError: null });
          window.location.href = '/';
        })
        .catch((errors) => {
          setProfilePostStatus({ isLoading: false, isError: true });
        });
    } else if (currentProgrammeId === programmes['lose-weight'].id) {
      setProfilePostStatus({ isLoading: true, isError: null });

      const requestData = mapCompleteProfileRequest(
        userId,
        currentJourney.id,
        undefined,
        undefined,
        signupFormValues
      );

      await completeProfile(requestData)
        .unwrap()
        .then(() => {
          setProfilePostStatus({ isLoading: false, isError: null });
          window.location.href = '/';
        })
        .catch((errors) => {
          setProfilePostStatus({ isLoading: false, isError: true });
        });
    } else {
      handleModalClose();
    }
  };

  return (
    <>
      <Helmet>
        <title>Ready Set Go! | Profile builder | Sign up | Gloji</title>
      </Helmet>
      <ModalPageWrapper>
        <ModalPageBody hasStickyFooter={false}>
          {user && user.given_name && !IsObjectEmptyOrNull(currentJourney) ? (
            <>
              <div className="ready-set-go-page-image__container">
                <ModalPageHeroImage
                  className="ready-set-go-page-image"
                  imageUrl={HeroImage}
                  altText="Veronika, gloji mentor"
                />
              </div>
              <ModalPageHeading size="large">Ready, set, go!</ModalPageHeading>
              <ModalPageCopy>
                <PageContent
                  programmeId={currentProgrammeId}
                  username={user.given_name}
                />
              </ModalPageCopy>
              <CustomModal
                open={open}
                onClose={handleModalClose}
                id="readySetGoModal"
                modalTitleId="readySetGoModalTitle"
                modalDescriptionId="readySetGoModalDescription"
              >
                <ModalContent id="readySetGoModalContent">
                  <div className="celebration-img-container"></div>
                  <ModalTitle id="readySetGoModalTitle">
                    Whoop! Whoop!
                  </ModalTitle>
                  <p className="modal-text" id="readySetGoModalDescription">
                    You’re ready to start using gloji.
                  </p>
                  {profilePostStatus.isError && (
                    <Alert severity="error" style={{ marginBottom: '1rem' }}>
                      Something went wrong!
                    </Alert>
                  )}
                  {profilePostStatus.isLoading ? (
                    <CircularLoader />
                  ) : (
                    <Button
                      className="lets-go-button"
                      variant="contained"
                      color="primary"
                      onClick={handleLetsGo}
                    >
                      Let's go
                    </Button>
                  )}
                </ModalContent>
              </CustomModal>
            </>
          ) : (
            <CircularLoader />
          )}
        </ModalPageBody>
        <ModalPageFooter hasStickyFooter={false}>
          <Button onClick={handleModalOpen} variant="contained" color="primary">
            I'm in
          </Button>
        </ModalPageFooter>
      </ModalPageWrapper>
    </>
  );
};

export default ReadySetGo;
